import React, { useCallback, useContext } from 'react'
import moment from 'moment'
import { useBedbankSelectDatesWithFlights } from 'hooks/OfferPage/useBedbankSelectDates'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { addToCheckoutCart, initBedbankSession } from 'actions/CheckoutActions'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { addToCart, addToCartMultiItem } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ModalContext from 'contexts/ModalContext'
import { generateBedbankAccommodationCheckoutItems } from 'lib/checkout/accommodation/cart'
import { dateDifference } from 'lib/datetime/dateUtils'
import { createBedbankHotelItemPayload } from 'tripPlanner/utils/itemConversions/cart/bedbankHotel'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import * as Analytics from 'analytics/analytics'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { useCreateTripItemBatch } from 'tripPlanner/hooks/api/tripItem'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import BedbankChangePriceModal from 'components/OfferPage/Bedbank/Modals/BedbankChangePriceModal'
import BedbankSoldOutModal from 'components/OfferPage/Bedbank/Modals/BedbankSoldOutModal'
import ProcessingModal from 'checkout/Components/Booking/Modals/ProcessingModal'
import { addToUserCart } from 'actions/AuthActions'
import { getSource } from 'selectors/offerSelectors'
import { fetchAccumulatedRatesForBedbankOffer, updateBedbankPricingData } from 'actions/BedbankOfferActions'
import { sum } from 'lib/array/arrayUtils'
import type { AppDispatch } from 'src/client/store'
import { allocateTravellersToRooms } from 'businessTraveller/utils/allocateTravellersToRooms'
import { floatify } from 'lib/maths/mathUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { useMultiRoomBookingGuard } from 'businessTraveller/hooks/useMultiRoomBookingGuard'

type SessionResponse = Awaited<ReturnType<typeof initBedbankSession>>

function isValidSession(session: SessionResponse): session is Extract<SessionResponse, { sessionId: string }> {
  return !!('sessionId' in session)
}

interface ChangedRate {
  rate: App.BedbankRate
  newGeneralPrice?: number
  newLuxPlusPrice?: number
  newPropertyFees?: number
  rooms: Array<App.Occupants>
}

function calculatePriceChange(changedRates: Array<ChangedRate>): {
  oldDisplayPrice: number
  newDisplayPrice: number
  oldPropertyFees: number
  newPropertyFees: number
} {
  const oldDisplayPrice = sum(changedRates, ch => {
    return ch.rate.totals.inclusive
  })
  const newDisplayPrice = sum(changedRates, ch => {
    return ch.newGeneralPrice ?? ch.rate.totals.inclusive
  })
  const oldPropertyFees = sum(changedRates, ch => ch.rate.totals.propertyFees ?? 0)
  const newPropertyFees = sum(changedRates, ch => ch.newPropertyFees ?? ch.rate.totals.propertyFees ?? 0)
  return { oldDisplayPrice, newDisplayPrice, oldPropertyFees, newPropertyFees }
}

/**
 * Dispatches an action to patch changed rates in the offer store and returns semi-patched rates
 * that can be used to perform a new bedbank service enquiry request.
 */
function updateChangedRates(dispatch: AppDispatch, { offerId, checkIn, checkOut, changedRates }: {
  offerId: string,
  checkIn: string,
  checkOut: string,
  changedRates: Array<ChangedRate>,
}): Array<App.BedbankRate> {
  return changedRates.map(({
    rate,
    newGeneralPrice,
    newLuxPlusPrice,
    newPropertyFees,
    rooms,
  }) => {
    const resolvedGeneralPrice = newGeneralPrice ?? rate.totals.inclusive
    const resolvedLuxPlusPrice = newLuxPlusPrice ?? rate.totals.memberInclusive ?? 0
    const resolvedPropertyFees = newPropertyFees ?? rate.totals.propertyFees ?? 0

    if (
      resolvedGeneralPrice === rate.totals.inclusive &&
      resolvedLuxPlusPrice === (rate.totals.memberInclusive ?? 0) &&
      resolvedPropertyFees === (rate.totals.propertyFees ?? 0)
    ) {
      // Not all rates have necessarily changed.
      return rate
    }

    dispatch(updateBedbankPricingData(
      offerId,
      rate.roomId,
      rate.id,
      rate.isFlightBundle,
      checkIn,
      checkOut,
      rooms,
      // NB: The price change error only tells us the total, not the room-level changes. To work
      // around this, we estimate by dividing by the number of rooms.
      {
        data: [{
          inclusive: resolvedGeneralPrice / rooms.length,
          // memberInclusive: resolvedLuxPlusPrice / rooms.length,
          propertyFees: resolvedPropertyFees / rooms.length,
        }],
      },
    ))

    // We don't need to be as thorough here; we only need to patch the fields that're used to
    // perform a bedbank service enquiry request.
    const newRate: App.BedbankRate = {
      ...rate,
      totals: {
        ...rate.totals,
        inclusive: resolvedGeneralPrice,
        memberInclusive: resolvedLuxPlusPrice,
        propertyFees: resolvedPropertyFees,
      },
    }

    return newRate
  })
}

function refreshSingleRoomRates({ dispatch, offerId, rooms, checkInDate, checkOutDate }: {
  dispatch: AppDispatch,
  offerId: string,
  rooms: Array<App.Occupants>,
  checkInDate: string,
  checkOutDate: string,
}) {
  dispatch(fetchAccumulatedRatesForBedbankOffer(offerId, rooms, checkInDate, checkOutDate, true))
}

interface SingleRoomArgs {
  offer: App.BedbankOffer
  packageView: App.BedbankOfferPackageView
  checkInDate?: string
  checkOutDate?: string
  rooms: Array<App.Occupants>
  isSpoofed?: boolean
  subscriber?: App.OrderSubscriber
  rateListId: string
  roomPosition: number
  roomListId: string
  flightPrice?: number
}

export function useBedbankMultiRoomBookNowCallback({
  offer,
  packageView,
  checkInDate,
  checkOutDate,
  rooms,
  isSpoofed,
  subscriber,
  rateListId,
  roomPosition,
  roomListId,
}: SingleRoomArgs) {
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const { mutate: createTripItems } = useCreateTripItemBatch()
  const tripPlannerTripId = useAppSelector(state => state.tripPlanner.currentTripId)
  const { data: currentTrip } = useTrip({ tripId: tripPlannerTripId })
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const source = useAppSelector(getSource)

  // Get business traveller information from Redux
  const selectedTravellerEmployeeIds = useAppSelector(state => state.businessTraveller.selectedTravellerEmployeeIds)
  const selectedTravellerGuestIds = useAppSelector(state => state.businessTraveller.selectedTravellerGuestIds)

  const showModal = useContext(ModalContext)
  const guardBookingFlow = useMultiRoomBookingGuard()

  const openDateSelector = useBedbankSelectDatesWithFlights(packageView.package.roomId)
  const onBook = useCallback(async(
    rate: App.BedbankRate,
    ratePosition: number,
    withFlights?: boolean,
    keepBrowsing = false,
  ) => {
    if (guardBookingFlow()) return

    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      trackEvent('BOOK_NOW_PRESSED')

      let closeProcessingModal: any
      showModal((resolve) => {
        closeProcessingModal = resolve
        return <ProcessingModal message="Please wait..." />
      })

      const pkg = packageView.package

      if (currentTrip) {
        const payload = createBedbankHotelItemPayload({
          offer,
          pkg,
          rate,
          rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          currentRegionCode,
          currencyCode: currency,
        })
        createTripItems({ tripId: currentTrip.id, tripItems: [payload] })
      }

      try {
        // For each room, create a separate session in parallel, so each room becomes a separate order item.
        const roomSessions = await Promise.all(rooms.map(async(room) => {
          // Per-room pricing
          const occupancyString = generateOccupancyStringByRoom(room)
          const roomPricing = rate.occupancyPricing.find(pricing => pricing.occupancy === occupancyString)

          const session = await initBedbankSession(dispatch, {
            propertyId: offer.id,
            roomTypeId: rate.roomId,
            roomRateId: rate.id,
            rateGroupId: rate.groupId,
            bedGroupId: rate.bedGroups[0].id,
            isFlightBundle: rate.isFlightBundle,
            csBooking: !!isSpoofed,
            region: currentRegionCode,
            rooms: [room],
            checkIn: checkInDate,
            checkOut: checkOutDate,
            price: roomPricing?.inclusive ?? floatify(rate.totals.inclusive / rooms.length),
            propertyFees: roomPricing?.propertyFees ?? (rate.totals.propertyFees ? floatify(rate.totals.propertyFees / rooms.length) : undefined),
            memberInclusive: roomPricing?.memberInclusive ?? (rate.totals.memberInclusive ? floatify(rate.totals.memberInclusive / rooms.length) : undefined),
            subscriber,
            source,
          })

          return {
            room,
            session,
          }
        }))

        closeProcessingModal()

        if (roomSessions.some(rs => !isValidSession(rs.session))) {
          // CHANGE OF PRICE - CANNOT PROCEED UNTIL USER ACCEPTS PRICE

          const changedRates = roomSessions.map(rs => ({
            rate,
            newGeneralPrice: rs.session.price,
            newPropertyFees: 'propertyFees' in rs.session ? rs.session.propertyFees : undefined,
            rooms: [rs.room],
          }))

          const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
            {...calculatePriceChange(changedRates)}
          />)
          if (hasUserAcceptedNewRates) {
            const newRates = updateChangedRates(dispatch, {
              offerId: offer.id,
              checkIn: checkInDate,
              checkOut: checkOutDate,
              changedRates,
            })
            // try book again with the updated rate
            // Book for multi-same-room bookings, so newRates[0] applies to all.
            onBook(newRates[0], ratePosition, withFlights, keepBrowsing)
            return
          }
          // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
          refreshSingleRoomRates({ dispatch, offerId: offer.id, rooms, checkInDate, checkOutDate })
          return
        }

        // All sessions valid - proceed with checkout
        if (roomSessions.every(rs => isValidSession(rs.session))) {
          // Allocate travellers to rooms
          const roomsWithTravellerInfo = allocateTravellersToRooms(
            rooms,
            selectedTravellerEmployeeIds,
            selectedTravellerGuestIds,
          )

          // Generate items with individual session IDs for each room.
          const bedbankItems = roomsWithTravellerInfo.map((roomOccupancy, index) => {
            const rs = roomSessions[index]
            const session = rs.session as Extract<SessionResponse, { sessionId: string }>
            const items = generateBedbankAccommodationCheckoutItems(
              offer,
              pkg,
              rate,
              {
                checkIn: checkInDate,
                checkOut: checkOutDate,
                duration: dateDifference(moment(checkOutDate).toDate(), moment(checkInDate).toDate()).days,
              },
              [roomOccupancy],
              session.sessionId,
            )
            return items[0]
          })

          dispatch(addToCheckoutCart([...bedbankItems]))

          fireEcommerceGTMEvent(addToCartGTM(offer, currency, rate.totals.inclusive, sumUpOccupancies(rooms), 'hotel'))

          Analytics.trackEvent(addToCart({
            type: 'bedbank',
            ratePosition,
            rateListId,
            roomPosition,
            roomListId,
            rate,
            isFlightBundle: !!withFlights,
          }))

          if (keepBrowsing) {
            showSnackbar('Your item has been added to the cart.', 'success')
            return
          }

          dispatch(navigateToCheckout())
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch(_error) {
        closeProcessingModal()
        // We've hit an unrecoverable error and expect at least one of the now-stale rates to be
        // blocklisted, so let's refresh.
        refreshSingleRoomRates({ dispatch, offerId: offer.id, rooms, checkInDate, checkOutDate })
        showModal(<BedbankSoldOutModal />)
      }
    }
  }, [checkInDate, checkOutDate, createTripItems, currency, currentRegionCode, currentTrip, dispatch, guardBookingFlow, isSpoofed, offer, openDateSelector, packageView.package, rateListId, roomListId, roomPosition, rooms, selectedTravellerEmployeeIds, selectedTravellerGuestIds, showModal, source, subscriber, trackEvent])

  return onBook
}

export function useBedbankSingleRoomAddToUserCartCallback({
  offer,
  packageView,
  checkInDate,
  checkOutDate,
  rooms,
  rateListId,
  roomPosition,
  roomListId,
}: SingleRoomArgs) {
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const { mutate: createTripItems } = useCreateTripItemBatch()
  const tripPlannerTripId = useAppSelector(state => state.tripPlanner.currentTripId)
  const { data: currentTrip } = useTrip({ tripId: tripPlannerTripId })
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)

  // Get business traveller information from Redux
  const selectedTravellerEmployeeIds = useAppSelector(state => state.businessTraveller.selectedTravellerEmployeeIds)
  const selectedTravellerGuestIds = useAppSelector(state => state.businessTraveller.selectedTravellerGuestIds)

  const guardBookingFlow = useMultiRoomBookingGuard()
  const openDateSelector = useBedbankSelectDatesWithFlights(packageView.package.roomId)

  const onAddToUserCart = useCallback(async(
    rate: App.BedbankRate,
    ratePosition: number,
    withFlights?: boolean,
  ) => {
    if (guardBookingFlow()) return
    if (!checkInDate || !checkOutDate) {
      openDateSelector(withFlights)
    } else {
      trackEvent('BOOK_NOW_PRESSED')

      const pkg = packageView.package

      if (currentTrip) {
        const payload = createBedbankHotelItemPayload({
          offer,
          pkg,
          rate,
          rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
          currentRegionCode,
          currencyCode: currency,
        })
        createTripItems({ tripId: currentTrip.id, tripItems: [payload] })
      }

      // Allocate travellers to rooms
      const roomsWithTravellerInfo = allocateTravellersToRooms(
        rooms,
        selectedTravellerEmployeeIds,
        selectedTravellerGuestIds,
      )

      const accommodationItems = generateBedbankAccommodationCheckoutItems(
        offer,
        pkg,
        rate,
        {
          checkIn: checkInDate,
          checkOut: checkOutDate,
          duration: dateDifference(moment(checkOutDate).toDate(), moment(checkInDate).toDate()).days,
        },
        roomsWithTravellerInfo,
      )

      fireEcommerceGTMEvent(addToCartGTM(offer, currency, rate.value, sumUpOccupancies(rooms), 'hotel'))

      const checkoutItems = [...accommodationItems]

      const bedbankContextData = {
        ratePosition,
        rateListId,
        roomPosition,
        roomListId,
        rate,
        isFlightBundle: !!withFlights,
      }

      Analytics.trackEvent(addToCartMultiItem(checkoutItems, { isMultiCart: true, bedbankContextData }))

      dispatch(addToUserCart(
        checkoutItems,
        false, // skipNotification
      ))
    }
  }, [checkInDate, checkOutDate, createTripItems, currency, currentRegionCode, currentTrip, dispatch, guardBookingFlow, offer, openDateSelector, packageView.package, rateListId, roomListId, roomPosition, rooms, selectedTravellerEmployeeIds, selectedTravellerGuestIds, trackEvent])

  return onAddToUserCart
}
